import { useEffect, useState, useCallback } from 'react'
import { cn } from '@/utils/cn'
import { getMessage } from '@/utils/messages'

interface LightboxPhoto {
  id: string
  blobUrl?: string
  name?: string
  mimeType?: string
}

interface PhotoLightboxProps {
  photos: LightboxPhoto[]
  initialIndex: number
  onClose: () => void
}

export function PhotoLightbox({ photos, initialIndex, onClose }: PhotoLightboxProps) {
  const [index, setIndex] = useState(initialIndex)
  const [expanded, setExpanded] = useState(false)

  const photo = photos[index]
  const hasPrev = index > 0
  const hasNext = index < photos.length - 1

  const prev = useCallback(() => { if (hasPrev) setIndex(i => i - 1) }, [hasPrev])
  const next = useCallback(() => { if (hasNext) setIndex(i => i + 1) }, [hasNext])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
      if (e.key === 'ArrowLeft') prev()
      if (e.key === 'ArrowRight') next()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose, prev, next])

  if (!photo) return null

  const isPdf = (photo.mimeType ?? '') === 'application/pdf'

  return (
    <div className="fixed inset-0 z-[600] flex items-center justify-center">
      <div
        className="absolute inset-0 bg-black/75 backdrop-blur-[3px]"
        onClick={onClose}
      />

      <div
        className={cn(
          'relative z-10 flex flex-col',
          'bg-[var(--surface)] border border-[var(--border)]',
          'rounded-[var(--radius)] shadow-[0_24px_72px_rgba(0,0,0,0.28)]',
          'animate-fade-in overflow-hidden',
          expanded
            ? 'w-[95vw] h-[93vh]'
            : 'w-full max-w-[680px] mx-4'
        )}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className={cn(
          'flex items-center justify-between flex-shrink-0',
          'px-5 py-3 border-b border-[var(--border)] bg-[var(--surface)]'
        )}>
          <div className="flex items-center gap-[10px] min-w-0">
            <span className="text-[15px]">{isPdf ? '📎' : '🖼️'}</span>
            <span
              className="text-[13px] font-semibold text-[var(--text)] truncate"
              style={{ fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif' }}
            >
              {photo.name ?? (isPdf ? getMessage('lightbox.fallback_document') : getMessage('lightbox.fallback_photo'))}
            </span>
            {photos.length > 1 && (
              <span className="text-[11px] text-[var(--text-muted)] font-mono flex-shrink-0">
                {index + 1} / {photos.length}
              </span>
            )}
          </div>

          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              type="button"
              onClick={() => setExpanded(e => !e)}
              title={expanded ? getMessage('lightbox.tooltip_collapse') : getMessage('lightbox.tooltip_expand')}
              className={cn(
                'w-7 h-7 rounded-[6px] border border-[var(--border)]',
                'bg-transparent text-[var(--text-muted)] text-[12px]',
                'flex items-center justify-center cursor-pointer',
                'hover:bg-[var(--surface3)] hover:text-[var(--text)]',
                'transition-all duration-150'
              )}
            >
              {expanded ? '⊡' : '⊞'}
            </button>

            <button
              type="button"
              onClick={onClose}
              className={cn(
                'w-7 h-7 rounded-[6px] border border-[var(--border)]',
                'bg-transparent text-[var(--text-muted)] text-[13px]',
                'flex items-center justify-center cursor-pointer',
                'hover:bg-[rgba(220,38,38,0.1)] hover:border-[rgba(220,38,38,0.4)] hover:text-[var(--error)]',
                'transition-all duration-150'
              )}
            >
              ✕
            </button>
          </div>
        </div>

        {/* Image / PDF area */}
        <div className={cn(
          'relative flex items-center justify-center bg-[#0d0d0d] overflow-hidden',
          expanded ? 'flex-1' : 'h-[420px]'
        )}>
          {isPdf ? (
            <iframe
              src={photo.blobUrl}
              title={photo.name ?? getMessage('lightbox.fallback_document')}
              className="w-full h-full border-0"
            />
          ) : (
            <img
              src={photo.blobUrl}
              alt={photo.name ?? getMessage('lightbox.fallback_photo')}
              className={cn(
                'max-w-full max-h-full object-contain select-none',
                'transition-all duration-200'
              )}
              draggable={false}
            />
          )}

          {hasPrev && (
            <button
              type="button"
              onClick={prev}
              className={cn(
                'absolute left-3 top-1/2 -translate-y-1/2',
                'w-9 h-9 rounded-full flex items-center justify-center',
                'bg-black/50 border border-white/15 text-white text-[16px]',
                'hover:bg-black/75 transition-all duration-150 cursor-pointer'
              )}
            >
              ‹
            </button>
          )}

          {hasNext && (
            <button
              type="button"
              onClick={next}
              className={cn(
                'absolute right-3 top-1/2 -translate-y-1/2',
                'w-9 h-9 rounded-full flex items-center justify-center',
                'bg-black/50 border border-white/15 text-white text-[16px]',
                'hover:bg-black/75 transition-all duration-150 cursor-pointer'
              )}
            >
              ›
            </button>
          )}
        </div>

        {/* Thumbnail strip */}
        {photos.length > 1 && (
          <div className={cn(
            'flex gap-[8px] px-4 py-3 overflow-x-auto flex-shrink-0',
            'border-t border-[var(--border)] bg-[var(--surface2)]'
          )}>
            {photos.map((p, i) => (
              <button
                key={p.id}
                type="button"
                onClick={() => setIndex(i)}
                className={cn(
                  'flex-shrink-0 w-[52px] h-[40px] rounded-[6px] overflow-hidden',
                  'border-2 transition-all duration-150 cursor-pointer',
                  i === index
                    ? 'border-[var(--accent)] opacity-100'
                    : 'border-[var(--border)] opacity-60 hover:opacity-90'
                )}
              >
                {(p.mimeType ?? '') === 'application/pdf' ? (
                  <div className="w-full h-full flex items-center justify-center bg-[var(--surface3)] text-[18px]">
                    📎
                  </div>
                ) : (
                  <img
                    src={p.blobUrl}
                    alt={p.name ?? getMessage('lightbox.fallback_photo')}
                    className="w-full h-full object-cover"
                    draggable={false}
                  />
                )}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
